import { useEffect, useState } from 'react'
import { createSocketConnection, socketLog, UPDATE_THROTTLE_MS } from './socketClient'
import { DEVICE_DASHBOARD_ID } from './deviceDashboardSocket'
import config from '../config/config'

const NSYNC_UPDATE_EVENT = 'nsyncUpdate'


export const useBmsSyncSocket = (assetId, bmsFilter, enabled = true) => {
  const [data, setData] = useState(null)
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!enabled || !assetId || !bmsFilter || !config.socketUrl) {
      setData(null)
      setConnected(false)
      return
    }

    
    const socket = createSocketConnection('bms-sync')
    if (!socket) return

    socket.on('connect', () => {
      const payload = { assetId, dashboardId: DEVICE_DASHBOARD_ID, bmsFilter }
      socketLog('[socket:bms-sync] subscribe ->', payload)
      socket.emit('subscribe', payload)
      setConnected(true)
    })
    socket.on('disconnect', () => setConnected(false))
  
    let lastUpdateAt = 0
    socket.on(NSYNC_UPDATE_EVENT, (payload) => {
      const now = Date.now()
      if (now - lastUpdateAt < UPDATE_THROTTLE_MS) return
      lastUpdateAt = now
      setData(payload)
    })

    return () => socket.disconnect()
  }, [assetId, bmsFilter, enabled])

  return { data, connected }
}

const FAULT_LABELS = {
  PCHG_FET: 'Precharge FET',
  DSG_FET: 'Discharge FET',
  CHG_FET: 'Charge FET',
}

const mapFaults = (faultNames) => {
  if (!Array.isArray(faultNames)) return undefined
  // No severity is reported by the backend for these — defaulting all to
  // "Fault" rather than guessing at "Critical" without evidence.
  return faultNames.map((name) => ({
    code: name.slice(0, 2).toUpperCase(),
    title: FAULT_LABELS[name] || name.replace(/_/g, ' '),
    severity: 'Fault',
  }))
}


const flagExtremes = (values) => {
  const max = Math.max(...values)
  const min = Math.min(...values)
  // All readings identical → nothing is an extreme, highlight nothing.
  if (max === min) return values.map(() => undefined)
  return values.map((v) => (v === max ? 'max' : v === min ? 'min' : undefined))
}

const mapCellVoltages = (m) => {
  if (!Array.isArray(m.cells) || m.cells.length === 0) return undefined
  const flags = flagExtremes(m.cells)
  return m.cells.map((mv, i) => ({
    label: `Cell ${i + 1}`,
    value: `${(mv / 1000).toFixed(3)} V`,
    flag: flags[i],
  }))
}

const mapNtcTemperatures = (m) => {
  if (!Array.isArray(m.ntc_C) || m.ntc_C.length === 0) return undefined
  const flags = flagExtremes(m.ntc_C)
  return m.ntc_C.map((c, i) => ({
    label: `NTC ${i + 1}`,
    value: `${c} °C`,
    flag: flags[i],
  }))
}

// Capacity counters arrive from the BMS in mAh; the cards display Ah. Divide by 1000 and round to 3 decimal places, but leave the raw value in the `raw` field for charting.
const mAhToAh = (mAh) => (mAh != null ? `${+(mAh / 1000).toFixed(3)} Ah` : undefined)


// Energy counters arrive from the BMS in Wh; the Capacity card's row is
// labelled "KWh spent", so convert rather than printing raw Wh under a kWh
// label. Same divide-and-round-to-3 treatment as mAhToAh above.
const whToKWh = (wh) => (wh != null ? `${+(wh / 1000).toFixed(3)} kWh` : undefined)

const cycleCountOf = (m) => {
  const counters = [m.chgCycles, m.dischgCycles].filter((v) => typeof v === 'number')
  return counters.length ? Math.min(...counters) : undefined
}


export const mapBmsItemToBmsDetail = (item) => {
  if (!item?.latest) return undefined
  const detail = mapNsyncUpdateToBmsDetail(
    { nodes: [{ ecuid: item._id, sourceAddress: item.sourceAddress, measurements: item.latest }] },
    item._id,
  )
  return detail ? { ...detail, organization: item.orgName } : undefined
}

export const mapNsyncUpdateToBmsDetail = (nsyncUpdate, ecuid) => {

  const node = nsyncUpdate?.nodes?.find((n) => n.ecuid === ecuid)
  if (!node) return undefined

  const m = node.measurements || {}
  const packVoltage = Array.isArray(m.cells) ? m.cells.reduce((sum, mv) => sum + mv, 0) / 1000 : undefined

  return {
    bmsId: node.ecuid,
    sourceAddress: node.sourceAddress,
    soc: m.soc_pct,
    soh: m.soh_pct,
    packVoltage: packVoltage !== undefined ? `${packVoltage.toFixed(1)} V` : undefined,
    packCurrent: m.current_A !== undefined ? `${m.current_A > 0 ? '+' : ''}${m.current_A} A` : undefined,
    chargeMosfet: m.fetStatus?.chg_on,
    dischargeMosfet: m.fetStatus?.dsg_on,
    prechargeMosfet: m.fetStatus?.pchg_on,
    firmwareVersion: m.fwVersion,
    protocol: m.protocolMajor !== undefined ? `${m.protocolMajor}.${m.protocolMinor}` : undefined,
    mcuType: m.mcuType,
    cycleCount: cycleCountOf(m),
    chargeCycle: m.chgCycles,
    dischargeCycle: m.dischgCycles,
    balancingFlags: m.balancingFlags,
    ahGained: mAhToAh(m.ahGained_mAh),
    ahSpent: mAhToAh(m.ahSpent_mAh),
    whSpent: whToKWh(m.whSpent),
    fullCapacity: mAhToAh(m.fullCapacity_mAh),
    remainingCap: mAhToAh(m.remainingCapacity_mAh),
    powerMode: m.powerMode === 1,
    batteryMode: m.batteryMode === 1,
    afeTemperature: m.afeTemp_C !== undefined ? `${m.afeTemp_C} °C` : undefined,
    boardTemperature: m.boardTemp_C !== undefined ? `${m.boardTemp_C} °C` : undefined,
    mosfetTemperature: m.mosfetTemp_C !== undefined ? `${m.mosfetTemp_C} °C` : undefined,
    prechargeTemperature: m.prechargeTemp_C !== undefined ? `${m.prechargeTemp_C} °C` : undefined,
    balancingTemperature: m.balancingTemp_C !== undefined ? `${m.balancingTemp_C} °C` : undefined,
    shuntTemperature: m.shuntTemp_C !== undefined ? `${m.shuntTemp_C} °C` : undefined,
    pchgFail: m.pchgFailReason,
    extraFault: m.faultExtra,
    cellVoltages: mapCellVoltages(m),
    ntcTemperatures: mapNtcTemperatures(m),
    
    cellsRaw: Array.isArray(m.cells) ? m.cells : undefined,
    ntcRaw: Array.isArray(m.ntc_C) ? m.ntc_C : undefined,
    raw: m,
    faults: mapFaults(m.faults),
  }
}
